import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ShoppingBag, Sparkles, Tag, ExternalLink, Percent } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SmartDiscounts() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('skinuse_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/deals`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => setDeals(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [headers]);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="smart-discounts-page">
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <ShoppingBag className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Marketplace</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">Smart Discounts</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              {user 
                ? 'Product deals personalized to your skin and hair concerns.'
                : 'Discover great deals on skincare and haircare products.'}
            </p>
          </div>

          {/* User Concerns Banner */}
          {user && (user.skin_concerns?.length > 0 || user.hair_concerns?.length > 0) && (
            <Card className="bg-gold/10 border-none mb-8" data-testid="concerns-banner">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-gold" />
                  <span className="font-semibold">Deals matched to your profile</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[...(user.skin_concerns || []), ...(user.hair_concerns || [])].map((concern, i) => (
                    <Badge key={i} variant="secondary">{concern}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Deals Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="border-none">
                  <Skeleton className="aspect-square" />
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-6 w-full mb-4" />
                    <Skeleton className="h-8 w-32" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No deals available at the moment.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {deals.map((deal) => (
                <Card key={deal.product_id} className="bg-card border-none shadow-card card-hover overflow-hidden group" data-testid={`deal-${deal.product_id}`}>
                  {/* Discount Badge */}
                  <div className="absolute top-4 left-4 z-10">
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-destructive text-destructive-foreground text-xs font-bold rounded-sm">
                      <Percent className="w-3 h-3" />
                      {deal.discount_percent}% OFF
                    </div>
                  </div>

                  {/* Image */}
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={deal.image_url}
                      alt={deal.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  <CardContent className="p-6">
                    {/* Brand */}
                    <p className="text-xs font-semibold uppercase tracking-wider text-gold mb-2">
                      {deal.brand}
                    </p>

                    {/* Name */}
                    <h3 className="font-serif text-lg font-semibold mb-2 line-clamp-2">
                      {deal.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {deal.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {deal.concerns?.slice(0, 3).map((concern, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {concern}
                        </Badge>
                      ))}
                    </div>

                    {/* Price */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl font-serif font-semibold text-gold">${deal.discounted_price.toFixed(2)}</span>
                      <span className="text-muted-foreground line-through">${deal.original_price.toFixed(2)}</span>
                    </div>

                    {/* CTA */}
                    <Button className="btn-primary w-full gap-2" data-testid={`deal-btn-${deal.product_id}`}>
                      <Tag className="w-4 h-4" />
                      Get Deal
                      <ExternalLink className="w-4 h-4 ml-auto" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
